import React from 'react';
import {
  Box,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
  TextField,
  TagBadge,
  EmptyState,
  TAG_COLORS,
  TAG_COLOR_OPTIONS,
  type TagColor,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  type TagWithStats,
} from '../../../hooks/useTags.js';

type TagsSectionProps = {
  isAdmin: boolean;
};

type TagFormState = {
  name: string;
  color: TagColor;
  description: string;
};

const emptyForm = (): TagFormState => ({
  name: '',
  color: 'blue',
  description: '',
});

export function TagsSection({ isAdmin }: TagsSectionProps) {
  const tagsQuery = useTagsQuery({ withStats: true });
  const createMutation = useCreateTagMutation();
  const updateMutation = useUpdateTagMutation();
  const deleteMutation = useDeleteTagMutation();

  const tags = (tagsQuery.data?.data ?? []) as TagWithStats[];
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [createForm, setCreateForm] = React.useState<TagFormState>(emptyForm);

  const [editingTag, setEditingTag] = React.useState<TagWithStats | null>(null);
  const [editForm, setEditForm] = React.useState<TagFormState>(emptyForm);

  React.useEffect(() => {
    if (isCreateOpen) {
      setCreateForm(emptyForm());
    }
  }, [isCreateOpen]);

  React.useEffect(() => {
    if (editingTag) {
      setEditForm({
        name: editingTag.name,
        color: editingTag.color as TagColor,
        description: editingTag.description ?? '',
      });
    }
  }, [editingTag]);

  const handleCreate = () => {
    if (!createForm.name.trim()) return;
    createMutation.mutate(
      {
        name: createForm.name.trim(),
        color: createForm.color,
        description: createForm.description.trim() || undefined,
      },
      {
        onSuccess: () => setIsCreateOpen(false),
      }
    );
  };

  const handleUpdate = () => {
    if (!editingTag) return;
    updateMutation.mutate(
      {
        id: editingTag.id,
        name: editForm.name.trim() || undefined,
        color: editForm.color,
        description: editForm.description.trim() || null,
      },
      {
        onSuccess: () => setEditingTag(null),
      }
    );
  };

  const handleDelete = (tagId: string, tagName: string) => {
    if (!window.confirm(`Delete the "${tagName}" tag? It will be removed from all items.`)) return;
    deleteMutation.mutate(tagId);
  };

  const ColorPicker = ({
    value,
    onChange,
  }: {
    value: TagColor;
    onChange: (color: TagColor) => void;
  }) => (
    <div>
      <Text size="sm" weight="medium" className="mb-2">
        Color
      </Text>
      <div className="flex flex-wrap gap-2">
        {TAG_COLOR_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={`w-8 h-8 rounded-full transition-all ${TAG_COLORS[option.id].dot} ${
              value === option.id ? 'ring-2 ring-offset-2 ring-primary' : ''
            }`}
            title={option.label}
          />
        ))}
      </div>
    </div>
  );

  const sortedTags = React.useMemo(
    () => tags.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [tags]
  );

  const columns = React.useMemo<ColumnDef<StockFeatures, TagWithStats>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Tag',
        cell: ({ row }) => (
          <TagBadge name={row.original.name} color={row.original.color as TagColor} />
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.description || '—'}
          </Text>
        ),
      },
      {
        accessorKey: 'assignmentCount',
        header: 'Usage',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.assignmentCount ?? 0} items
          </Text>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Flex gap="sm" justify="end">
            <Button
              variant="outline"
              size="sm"
              onPress={() => setEditingTag(row.original)}
              isDisabled={!isAdmin}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onPress={() => handleDelete(row.original.id, row.original.name)}
              isDisabled={!isAdmin || deleteMutation.isPending}
            >
              Delete
            </Button>
          </Flex>
        ),
      },
    ],
    [isAdmin, deleteMutation.isPending]
  );

  return (
    <Box className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Heading level={2}>Tags</Heading>
          <Text size="sm" className="text-muted-foreground">
            Create and manage tags to organize issues and workflows.
          </Text>
        </div>
        <DialogTrigger isOpen={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <Button isDisabled={!isAdmin}>Add Tag</Button>
          <Dialog title="Add Tag">
            <DialogContent>
              <Box className="space-y-4">
                <TextField
                  label="Name"
                  value={createForm.name}
                  onChange={(value) => setCreateForm((prev) => ({ ...prev, name: value }))}
                  isRequired
                  maxLength={50}
                />
                <ColorPicker
                  value={createForm.color}
                  onChange={(color) => setCreateForm((prev) => ({ ...prev, color }))}
                />
                <div className="mt-2">
                  <Text size="sm" color="muted">
                    Preview:
                  </Text>
                  <div className="mt-1">
                    <TagBadge name={createForm.name || 'Tag Name'} color={createForm.color} />
                  </div>
                </div>
                <TextField
                  label="Description (optional)"
                  value={createForm.description}
                  onChange={(value) => setCreateForm((prev) => ({ ...prev, description: value }))}
                  maxLength={200}
                />
              </Box>
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onPress={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                onPress={handleCreate}
                isDisabled={!createForm.name.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>
      </div>

      {tagsQuery.isLoading ? (
        <Flex align="center" justify="center" className="py-12">
          <LoadingOrbit size="md" />
        </Flex>
      ) : tags.length === 0 ? (
        <EmptyState
          title="No tags created"
          description="Use the Add Tag button to create your first tag."
        />
      ) : (
        <DataTable columns={columns} data={sortedTags} aria-label="Tags table" />
      )}

      {/* Edit Dialog */}
      <DialogTrigger
        isOpen={Boolean(editingTag)}
        onOpenChange={(open) => !open && setEditingTag(null)}
      >
        <span />
        <Dialog title="Edit Tag">
          <DialogContent>
            <Box className="space-y-4">
              <TextField
                label="Name"
                value={editForm.name}
                onChange={(value) => setEditForm((prev) => ({ ...prev, name: value }))}
                isRequired
                maxLength={50}
              />
              <ColorPicker
                value={editForm.color}
                onChange={(color) => setEditForm((prev) => ({ ...prev, color }))}
              />
              <div className="mt-2">
                <Text size="sm" color="muted">
                  Preview:
                </Text>
                <div className="mt-1">
                  <TagBadge name={editForm.name || 'Tag Name'} color={editForm.color} />
                </div>
              </div>
              <TextField
                label="Description (optional)"
                value={editForm.description}
                onChange={(value) => setEditForm((prev) => ({ ...prev, description: value }))}
                maxLength={200}
              />
            </Box>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setEditingTag(null)}>
              Cancel
            </Button>
            <Button
              onPress={handleUpdate}
              isDisabled={!editForm.name.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
